import type { FiltersState } from '@components/data-table-filter/core/types';
import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { parseAsInteger, parseAsJson, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORTING: SortingState = [{ desc: false, id: 'name' }];

type AdminOrganizationsQueryParams = {
  filters?: FiltersState;
  limit: number;
  page: number;
  search?: string;
};

type UseAdminOrganizationsQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  filtersState: FiltersState;
  handleLimitChange: (newLimit: number) => void;
  limit: number;
  page: number;
  queryParams: AdminOrganizationsQueryParams;
  search: string | null;
  selectedOrganizationId: string | null;
  setFiltersState: (filters: FiltersState) => Promise<URLSearchParams>;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string | null) => Promise<URLSearchParams>;
  setSelectedOrganizationId: (orgId: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState | null) => Promise<URLSearchParams>;
  sortingState: SortingState;
};

export const useAdminOrganizationsQueryParams = (): UseAdminOrganizationsQueryParamsReturn => {
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_PAGE_SIZE));
  const [search, setSearch] = useQueryState('search', parseAsString);
  const [selectedOrganizationId, setSelectedOrganizationId] = useQueryState('orgId', parseAsString);
  const [sorting, setSorting] = useQueryState('sorting', parseAsSortingState.withDefault(DEFAULT_SORTING));
  const [filtersState, setFiltersState] = useQueryState<FiltersState>(
    'filters',
    parseAsJson(z.custom<FiltersState>()).withDefault([]),
  );

  const sortingState: SortingState = sorting ?? DEFAULT_SORTING;

  const queryParams = useMemo(() => {
    return {
      filters: filtersState.length > 0 ? filtersState : undefined,
      limit: limit ?? DEFAULT_PAGE_SIZE,
      page: page ?? 1,
      search: search ?? undefined,
    };
  }, [filtersState, limit, page, search]);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      void setLimit(newLimit);
      void setPage(1);
    },
    [setLimit, setPage],
  );

  return {
    DEFAULT_SORTING,
    filtersState,
    handleLimitChange,
    limit: limit ?? DEFAULT_PAGE_SIZE,
    page: page ?? 1,
    queryParams,
    search,
    selectedOrganizationId,
    setFiltersState,
    setPage,
    setSearch,
    setSelectedOrganizationId,
    setSorting,
    sortingState,
  };
};
